import placeholderDark from 'assets/placeholder-logo-dark.png';
import placeholderLight from 'assets/placeholder-logo-light.png';
import { useTheme } from 'contexts/ThemeContext';

/** Intrinsic aspect ratio (width / height) of the placeholder artwork. */
export const PLACEHOLDER_ASPECT = 1220 / 380;

/**
 * The "ADD YOUR LOGO" mark shown for a center or organization that has not
 * uploaded one. Uploading stays optional — this is what fills the slot in the
 * meantime, so a tenant without branding reads as "not set up yet" rather than
 * as an empty box.
 *
 * Variant naming follows `Logo.jsx`: 'dark' is the dark artwork, for light
 * backgrounds; 'light' is the white artwork, for dark ones. Both are drawn on
 * transparency, so they need the surrounding surface to supply contrast.
 *
 * Use `usePlaceholderLogoSrc()` where the caller already owns the <img> (an
 * upload preview that swaps between the real logo and this one); use the
 * component where it is the whole slot.
 */
export function usePlaceholderLogoSrc() {
  const { isDark } = useTheme();
  return isDark ? placeholderLight : placeholderDark;
}

export default function PlaceholderLogo({ height = 36, maxWidth, className = '' }) {
  const src = usePlaceholderLogoSrc();
  return (
    <img
      src={src}
      alt="Add your logo"
      // Width follows the artwork's own ratio so the mark never stretches, and
      // is capped so a wide slot does not blow it up past the real logo's size.
      style={{ height, width: Math.round(height * PLACEHOLDER_ASPECT), maxWidth }}
      className={`block max-w-full shrink-0 object-contain select-none ${className}`}
      draggable={false}
    />
  );
}
